import type { AxiosError } from "axios";
import type {
  DefaultOptions,
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";

/**
 * Extract the return type from an async function
 */
export type ApiFnReturnType<FnType extends (...args: never[]) => Promise<unknown>> =
  Awaited<ReturnType<FnType>>;

/**
 * Configuration type for query hooks
 * Allows customizing query behavior while maintaining type safety
 */
export type QueryConfig<
  QueryFnType extends (...args: never[]) => Promise<unknown>,
  TSelectData = ApiFnReturnType<QueryFnType>,
> = Omit<
  UseQueryOptions<
    ApiFnReturnType<QueryFnType>,
    AxiosError,
    TSelectData
  >,
  "queryKey" | "queryFn"
>;

/**
 * Configuration type for mutation hooks
 * Allows customizing mutation behavior while maintaining type safety
 */
export type MutationConfig<
  MutationFnType extends (...args: never[]) => Promise<unknown>,
> = UseMutationOptions<
  ApiFnReturnType<MutationFnType>,
  AxiosError,
  Parameters<MutationFnType>[0]
>;

/**
 * Default query client options
 */
export const queryConfig: DefaultOptions = {
  queries: {
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 60 * 1000, // 1 minute
  },
  mutations: {
    retry: 0,
  },
};
